import React, {Component, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import SilverButton from '../../../../../../../../components-new/buttons/SilverButton';
import {addNotification} from '../../../../../../../../services/notification/action';
import WebChatDemo from './WebChatDemo';
import Translate from '../../../../../../../../components/Translate';
import config from '../../../../../../../../config';
import Input from "../../../../../../../../components-new/form/Input";
import Field from "../../../../../../../../components-new/form/Field";
import ColorInput from "../../../../../../../../components/ColorInput";
import Switch from "../../../../../../../../components-new/form/Switch";
import ChoiceButtons from "../../../../../../../../components-new/form/ChoiceButtons";
import CustomInputs from './CustomInputs';
import LogoUpload from './LogoUpload';
import '../WebChatDesign.css';

// Keep these aligned with WebChatConfDocument.Colors defaults.
const DEFAULT_COLORS = {
    headerBackground: '#043e5c',
    bodyBackground: '#F2F5FC',
    botMessageBackground: '#ffffff',
    userMessageBackground: '#043e5c',
    footerBackground: '#ffffff',
    botTextColor: '#09183A',
    userTextColor: '#FFFFFF',
    quickReplyTextColor: '#09183A',
};

const ResetColorsButton = connect(null, {addNotification})(({onSave, addNotification}) => {
    const [saving, setSaving] = useState(false);
    const reset = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await onSave(DEFAULT_COLORS);
            addNotification('webChatColorsResetSuccess', config.notificationLevels.success);
        } catch (error) {
            addNotification('webChatColorsResetError', config.notificationLevels.error);
        } finally {
            setSaving(false);
        }
    };
    return <SilverButton type="button" width="auto" loading={saving} onClick={reset}>
        <Translate text="webChatColorsReset"/>
    </SilverButton>;
});

/*
 * A piece of the chat's wording, with its own limit counted as it is typed.
 *
 * Every field on this screen is capped - the chat has only so much room for a
 * button label or a menu item - and before this the cap was silent: the field
 * simply stopped accepting letters. The count says how much room is left.
 *
 * It saves when the field is left, as it always did, so a half-typed word is
 * never sent to the server.
 */
const TextSetting = ({field, value, maxLength, onSave, disabled, help, wide, trailing}) => {
    const [draft, setDraft] = useState(value || '');

    /* the server is the authority: a save elsewhere on the screen re-sends these */
    useEffect(() => {
        setDraft(value || '');
    }, [value]);

    return (
        <Field
            className={wide ? 'stg-grid__wide' : ''}
            label={field}
            help={help}
            required={!field.endsWith('LabelText')}
            htmlFor={'wc-' + field}
            counter={draft.length + '/' + maxLength}
        >
            <div className="stg-control-row">
                <Input
                    id={'wc-' + field}
                    className="stg-input"
                    maxLength={maxLength}
                    name={field}
                    value={draft}
                    onChange={event => setDraft(event.target.value)}
                    onBlur={() => onSave(field)(draft)}
                    disabled={disabled}
                />
                {trailing}
            </div>
        </Field>
    );
};

TextSetting.propTypes = {
    field: PropTypes.string.isRequired,
    maxLength: PropTypes.number.isRequired,
    onSave: PropTypes.func.isRequired,
};

/* a setting that is only on or off, with what it does written under its name */
const ToggleSetting = ({name, note, checked, onChange, disabled}) => (
    <div className={'stg-toggle ' + (disabled ? 'disabled' : '')}>
        <div className="stg-toggle__text">
            <div className="stg-toggle__name"><Translate text={name}/></div>
            <p className="stg-toggle__note"><Translate text={note}/></p>
        </div>
        <Switch checked={checked} onChange={onChange} disabled={disabled}/>
    </div>
);

class WebChatStyles extends Component {
    maxLengths = {
        headerText: 20,
        messageText: 17,
        getStartedText: 25,
        profileInfoInputsButtonText: 35,
        profileInfoInputsCancelButtonText: 35,
        provideContactInfoDescription: 120,
        provideContactInfoPhonePrefix: 8,
        newChatText: 25,
        generateChatHistoryText: 25,
        muteOrUnmuteText: 25,
        screenshotText: 25,
        openNewWindowText: 25,
        input1PlaceholderText: 15,
        input1LabelText: 50,
        input2LabelText: 50,
        input3LabelText: 50,
        input4LabelText: 50,
        input2PlaceholderText: 15,
        input3PlaceholderText: 15,
        input4PlaceholderText: 15,
        inputHeaderText: 200,
        fileSizeErrorNotificationText: 30,
        invalidEmailNotificationText: 30,
        invalidPhoneNumberNotificationText: 30,
    };

    /* the fields that want the whole row: long sentences, not labels */
    wideFields = ['inputHeaderText'];

    fields = {
        main: ['headerText', 'messageText', 'getStartedText', 'profileInfoInputsButtonText', 'profileInfoInputsCancelButtonText', 'provideContactInfoDescription', 'provideContactInfoPhonePrefix'],
        menu: ['newChatText', 'generateChatHistoryText', 'muteOrUnmuteText', 'screenshotText', 'openNewWindowText'],
        infoInputs: ['inputHeaderText', 'input1PlaceholderText', 'input1LabelText', 'input2PlaceholderText', 'input2LabelText', 'input3PlaceholderText', 'input3LabelText', 'input4PlaceholderText', 'input4LabelText'],
        notifications: ['fileSizeErrorNotificationText', 'invalidEmailNotificationText', 'invalidPhoneNumberNotificationText'],
        colors: ['headerBackground', 'botMessageBackground', 'botTextColor', 'userTextColor', 'userMessageBackground', 'quickReplyTextColor', 'bodyBackground', 'footerBackground'],
    };

    saveColor = (position) => (value) => {
        const {saveColors} = this.props;
        const data = {[position]: typeof value === 'object' ? value.target.value : value};
        saveColors(data);
    };

    saveText = (position) => (value) => {
        const {saveTexts} = this.props;
        const textValue = typeof value === 'object' ? value.target.value : value;
        if (textValue.length <= this.maxLengths[position]) {
            const data = {[position]: textValue};
            saveTexts(data);
        }
    };

    renderTextSetting = (field, disabled, trailing) => (
        <TextSetting
            key={field}
            field={field}
            value={this.props.texts[field]}
            maxLength={this.maxLengths[field]}
            onSave={this.saveText}
            help={field + 'Help'}
            disabled={disabled}
            trailing={trailing}
            wide={this.wideFields.indexOf(field) !== -1}
        />
    );

    /*
     * A section says what it is for, not where you are. Where you are is on the
     * tab above, so a tab with one section in it gets the explanation and no
     * heading - a heading there would only repeat the tab's own name.
     */
    renderSectionHead = (title, note, aside) => (
        <div className="stg-card__head">
            <div>
                {title && <h2 className="stg-card__title"><Translate text={title}/></h2>}
                <p className="stg-card__note"><Translate text={note}/></p>
            </div>
            {aside}
        </div>
    );

    renderColorTab = () => {
        const {colors, texts} = this.props;
        return (
            <div className="stg-page">
                <div className="stg-card wc-colors-card">
                    {/*{this.renderSectionHead(null, 'webChatColorsNote')}*/}

                    <LogoUpload logoUrl={colors.logoUrl} onSave={this.props.saveColors}/>
                    <div className="wc-colors">
                        <div className="wc-colors__preview">
                            <WebChatDemo {...colors} headerText={texts.headerText}/>
                        </div>

                        <div className="wc-colors__list">
                            <ResetColorsButton onSave={this.props.saveColors}/>
                            {this.fields.colors.map((color) => (
                                <Field
                                    key={color}
                                    label={'color' + color.charAt(0).toUpperCase() + color.slice(1)}
                                >
                                    <ColorInput value={colors[color]} handleChange={this.saveColor(color)}/>
                                </Field>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    renderMainTab = () => {
        const {
            texts,
            poweredByConfig,
            saveTypingIndicatorConfig,
            typingIndicatorEnabled,
            seenIndicatorEnabled,
            saveSeenIndicatorConfig,
            operatorPhotoInChat,
            saveOperatorPhotoInChat,
            botPhotoInChat,
            saveBotPhotoInChat,
            voiceMessagesEnabled,
            saveVoiceMessagesConfig,
            endButton,
            saveEndButton,
            userReactionsEnabled,
            saveUserReactionsConfig,
            features
        } = this.props;

        return (
            <div className="stg-page web-chat-main-page">
                <div className="stg-card">
                    {this.renderSectionHead(null, 'webChatTextsNote')}

                    <div className="stg-grid">
                        {texts.isCompleted && this.fields.main.map(field => this.renderTextSetting(field, false))}
                    </div>
                </div>

                <div className="stg-card">
                    {this.renderSectionHead('webChatPositioning', 'webChatPositioningNote')}
                    <Field label="webChatDefaultPosition">
                        <ChoiceButtons
                            value={this.props.defaultPosition}
                            onChange={value => this.props.savePositioning({defaultPosition: value})}
                            options={['BOTTOM_LEFT', 'BOTTOM_RIGHT'].map(value => ({
                                value, label: <Translate text={'webChatPosition' + value}/>
                            }))}
                        />
                    </Field>
                    <div className="stg-toggles" style={{marginTop: '16px'}}>
                        <ToggleSetting name="webChatDragEnabled" note="webChatDragEnabledNote"
                                       checked={this.props.dragEnabled}
                                       onChange={value => this.props.savePositioning({dragEnabled: value})}/>
                    </div>
                </div>

                <div className="stg-card">
                    {this.renderSectionHead('webChatFeatures', 'webChatFeaturesNote')}

                    <div className="stg-toggles">
                        <ToggleSetting
                            name="operatorPhotoInChat"
                            note="operatorPhotoInChatNote"
                            checked={operatorPhotoInChat}
                            onChange={saveOperatorPhotoInChat}
                        />
                        <ToggleSetting
                            name="botPhotoInChat"
                            note="botPhotoInChatNote"
                            checked={botPhotoInChat}
                            onChange={saveBotPhotoInChat}
                        />
                        <ToggleSetting
                            name="voiceMessagesEnabled"
                            note="voiceMessagesEnabledNote"
                            checked={voiceMessagesEnabled}
                            onChange={saveVoiceMessagesConfig}
                        />
                        <ToggleSetting
                            name="endButtonPresence"
                            note="endButtonPresenceNote"
                            checked={endButton}
                            onChange={saveEndButton}
                        />
                        <ToggleSetting
                            name="typingIndicatorPresence"
                            note="typingIndicatorPresenceNote"
                            checked={typingIndicatorEnabled}
                            onChange={saveTypingIndicatorConfig}
                        />
                        <ToggleSetting
                            name="seenIndicatorPresence"
                            note="seenIndicatorPresenceNote"
                            checked={seenIndicatorEnabled}
                            onChange={saveSeenIndicatorConfig}
                        />

                        {features.includes(config.features.MESSAGE_REACTIONS) &&
                            <ToggleSetting
                                name="userReactionsPresence"
                                note="userReactionsPresenceNote"
                                checked={userReactionsEnabled}
                                onChange={saveUserReactionsConfig}
                            />
                        }

                        {/* part of the plan rather than a choice - see poweredByNote */}
                        <ToggleSetting
                            name="poweredBy"
                            note="poweredByNote"
                            checked={poweredByConfig}
                            onChange={() => {}}
                            disabled
                        />
                    </div>
                </div>
            </div>
        );
    };

    renderMenuTab = () => {
        const {texts, menuItems, saveMenuItemConfig} = this.props;
        return (
            <div className="stg-page">
                <div className="stg-card">
                    {this.renderSectionHead(null, 'webChatMenuNote')}

                    <div className="stg-grid">
                        {this.fields.menu.map(field => this.renderTextSetting(
                            field,
                            menuItems ? !menuItems[field] : false,
                            menuItems && (
                                <Switch
                                    checked={menuItems[field]}
                                    onChange={(value) => saveMenuItemConfig(field, value)}
                                />
                            )
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    renderInfoInputsTab = () => {
        const {
            profileInfoInputsEnabled,
            profileInfoInputsType,
            saveProfileInfoInputsConfig,
            saveProfileInfoInputsType,
            profileInfoInputs,
            saveProfileInfoInputConfig
        } = this.props;
        const typeOptions = [
            {value: 'ON_START', label: 'profileInfoInputsOnStart'},
            {value: 'ON_OPERATOR_REQUEST', label: 'profileInfoInputsOnOperatorRequest'},
            {value: 'ON_OUT_OF_WORKING_HOURS', label: 'profileInfoInputsOnOutOfWorkingHours'}
        ];

        return (
            <div className="stg-page">
                <div className="stg-card">
                    {this.renderSectionHead(
                        null,
                        'webChatInfoInputsNote',
                        <Switch checked={profileInfoInputsEnabled} onChange={saveProfileInfoInputsConfig}/>
                    )}

                    <div className="stg-grid">
                        <Field
                            className="stg-grid__wide"
                            label="profileInfoInputsType"
                            help="profileInfoInputsTypeHelp"
                        >
                            <ChoiceButtons
                                options={typeOptions.map(option => ({
                                    ...option,
                                    label: <Translate text={option.label}/>
                                }))}
                                value={profileInfoInputsType || 'ON_START'}
                                onChange={saveProfileInfoInputsType}
                                disabled={!profileInfoInputsEnabled}
                                styles={{width: '100%'}}
                            />
                        </Field>

                        {this.renderTextSetting('inputHeaderText', !profileInfoInputsEnabled)}

                        {['input1', 'input2', 'input3', 'input4'].map(item => {
                            const off = !profileInfoInputsEnabled || (profileInfoInputs && !profileInfoInputs[item]);
                            return (
                                <div className="wc-info-input-card stg-grid__wide" key={item}>
                                    <div className="wc-info-input-card__head">
                                        <h3><Translate text={item + 'PlaceholderText'}/></h3>
                                        {profileInfoInputs && (
                                            <Switch
                                                disabled={!profileInfoInputsEnabled}
                                                checked={profileInfoInputs[item]}
                                                onChange={value => saveProfileInfoInputConfig(item, value)}
                                            />
                                        )}
                                    </div>
                                    <div className="stg-grid">
                                        {this.renderTextSetting(item + 'LabelText', off)}
                                        {this.renderTextSetting(item + 'PlaceholderText', off)}
                                    </div>
                                </div>
                            );
                        })}
                        {['ON_OPERATOR_REQUEST', 'ON_OUT_OF_WORKING_HOURS'].includes(profileInfoInputsType) && (
                            <CustomInputs
                                inputs={this.props.customInputs}
                                disabled={!profileInfoInputsEnabled}
                                onSave={this.props.saveCustomInputs}
                            />
                        )}
                    </div>
                </div>
            </div>
        );
    };

    renderNotificationsTab = () => (
        <div className="stg-page">
            <div className="stg-card">
                {this.renderSectionHead(null, 'webChatNotificationsNote')}

                <div className="stg-grid">
                    {this.fields.notifications.map(field => this.renderTextSetting(field, false))}
                </div>
            </div>
        </div>
    );

    render() {
        const {features, activeTab} = this.props;

        return (
            <div className="web-chat-styles">
                {features.includes(config.features.CUSTOMIZE_WEBCHAT_COLORS) && activeTab === 'colors' && this.renderColorTab()}
                {features.includes(config.features.CUSTOMIZE_WEBCHAT) && (
                    <React.Fragment>
                        {activeTab === 'main' && this.renderMainTab()}
                        {activeTab === 'menu' && this.renderMenuTab()}
                        {activeTab === 'infoInputs' && this.renderInfoInputsTab()}
                        {activeTab === 'notifications' && this.renderNotificationsTab()}
                    </React.Fragment>
                )}
            </div>
        );
    }
}

WebChatStyles.propTypes = {
    defaultPosition: PropTypes.string,
    dragEnabled: PropTypes.bool,
    savePositioning: PropTypes.func,
    customInputs: PropTypes.array,
    saveCustomInputs: PropTypes.func,
    colors: PropTypes.object.isRequired,
    texts: PropTypes.object.isRequired,
    saveColors: PropTypes.func.isRequired,
    saveTexts: PropTypes.func.isRequired,
    features: PropTypes.array.isRequired,
    poweredByConfig: PropTypes.bool,
    typingIndicatorEnabled: PropTypes.bool,
    savePoweredByConfig: PropTypes.func,
    saveTypingIndicatorConfig: PropTypes.func,
    operatorPhotoInChat: PropTypes.bool,
    botPhotoInChat: PropTypes.bool,
    voiceMessagesEnabled: PropTypes.bool,
    saveOperatorPhotoInChat: PropTypes.func,
    endButton: PropTypes.bool,
    saveEndButton: PropTypes.func,
    profileInfoInputsEnabled: PropTypes.bool,
    profileInfoInputsType: PropTypes.string,
    saveProfileInfoInputsConfig: PropTypes.func,
    saveProfileInfoInputsType: PropTypes.func,
    menuItems: PropTypes.object,
    profileInfoInputs: PropTypes.object,
    saveProfileInfoInputConfig: PropTypes.func,
    saveMenuItemConfig: PropTypes.func,
    activeTab: PropTypes.string.isRequired,
};

export default WebChatStyles



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/webChatDesignAndConfig/components/WebChatStyles.jsx